import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FeatureFlag, FeatureFlagProvider, FeatureFlagUser } from './feature-flag.interfaces';
import { AuthDataService } from '@uzurv/http';
import { AmplitudeFeatureFlagProvider } from './amplitude-feature-flag.provider';
import { CacheKeyConstants } from '@uzurv/core';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private provider: FeatureFlagProvider;
  private flagsSubject = new BehaviorSubject<Record<string, FeatureFlag>>({});

  constructor(private authDataService: AuthDataService, private injector: Injector) {
    this.initializeProvider();
  }

  private initializeProvider(): void {
    this.provider = this.injector.get(AmplitudeFeatureFlagProvider);
  }

  initializeFeatureFlags(): Observable<void> {
    return from(this.provider.initialize());
  }

  fetchFeatureFlags(): Observable<Record<string, FeatureFlag>> {
    const user: FeatureFlagUser = {
      userId: String(this.authDataService?.currentUser?.username)
    };

    return from(this.provider.fetch(user)).pipe(
      tap(flags => {
        this.flagsSubject.next(flags);
      })
    );
  }

  writeCurrentFlagsToCache(flags: Record<string, FeatureFlag>): void {
    localStorage.setItem(CacheKeyConstants.FEATURE_FLAGS, JSON.stringify(flags));
  }

  readCurrentFlagsFromCache(): Record<string, FeatureFlag> {
    const cached = localStorage.getItem(CacheKeyConstants.FEATURE_FLAGS);
    if (!cached) {
      return {};
    }

    try {
      const flags = JSON.parse(cached);
      this.flagsSubject.next(flags);
      return flags;
    } catch (error) {
      return {};
    }
  }

  trackExposure(flagCode: string) {
    this.provider.trackExposure(flagCode);
  }
}
